<template>
  <div class="container">
    <h1>Terms and Conditions</h1>
    <hr />
    <p>
      Wrytopia is an online platform dedicated to writing services. Within
      Wrytopia, writers have the opportunity to access tailored outline writing
      services that align with your unique writing needs. Throughout this
      agreement, the terms "we," "us," and "Wrytopia" all refer to this
      platform.
    </p>
    <p>
      Within this document, you will find the stipulations outlining how
      Wrytopia delivers its writing services via the Wrytopia website. Upon
      accepting the terms of this agreement or utilizing the services provided
      by the platform, you are consenting to adhere to the Terms and Conditions
      detailed herein, as well as all terms that are referenced and integrated
      by association.
    </p>
    <br />
    <ol>
      <div>
        <li class="bold">Accepting these Terms</li>
        <p>
          This agreement establishes a legally binding contract between us and
          you, whether you are acting on an individual basis or as an authorized
          representative for an author, writer, publisher, or corporation. In
          the latter scenario, the term "you" pertains to the entity—author,
          writer, publisher, or corporation—on whose behalf you are authorized
          to act. It is important to thoroughly review this agreement and seek
          advice from your own business and legal advisors prior to using the
          services or agreeing to its terms.
        </p>
        <p>
          Moreover, upon agreeing to the terms and conditions outlined in this
          document, you verify that you have comprehensively gone through and
          grasped our
          <a href="/privacy-policy" class="link-success">Privacy Policy</a>.
          Should you disagree with these terms, you are prohibited from
          accessing or utilizing the service, and you must promptly discontinue
          any such usage. Alongside these conditions, you also commit to
          adhering to any extra policies and terms associated with the service,
          as well as any additional regulations, policies, and processes that
          might be periodically disclosed on the services. All of these are
          considered an integral part of this agreement.
        </p>
      </div>
      <div>
        <li class="bold">User Eligibilty</li>
        <p>
          Wrytopia is available to individuals who are at least 18 years of age.
          By accessing and using our platform, users confirm that they meet this
          age requirement. Additionally, the service is intended for
          individuals, authors, writers, publishers, and corporations seeking
          professional writing assistance. Users must also have the legal
          capacity to enter into binding agreements in their respective
          jurisdictions. The use of our online writing service by any person or
          entity that does not meet these eligibility criteria is strictly
          prohibited. We reserve the right to request verification of age and
          eligibility and to suspend or terminate accounts of users who do not
          meet these requirements.
        </p>
      </div>
      <div>
        <li class="bold">User Responsibilities</li>
        <p>
          As users of our online writing service, you hold the responsibility to
          ensure that any content you submit or utilize adheres to ethical and
          legal standards. You affirm and consent that your utilization of the
          website and services shall not:
        </p>
        <ul>
          <li>
            In any manner contravene any relevant federal, state, local, or
            global law or regulation, including, but not restricted to, laws
            related to data or software export, patents, trademarks, trade
            secrets, copyrights, or other intellectual property rights. Your
            actions should also not infringe upon the legal rights of others,
            such as publicity and privacy rights, nor should they encompass
            content that could potentially lead to civil or criminal penalties
            under applicable laws or regulations. Additionally, your actions
            should not be at odds with these Terms and our Privacy Policy;
          </li>
          <li>
            Cause harm to, or cause disruption to any aspect of the website, any
            associated infrastructure, including equipment and networks, any
            software integral to the functioning of the website or application,
            or any equipment, network, or software belonging to a third party;
          </li>
          <li>
            Incorporate or consist of content that is exploitative, indecent,
            injurious, menacing, abusive, distressing, malevolent, libelous,
            sexually explicit or adult-oriented, aggressive, incendiary, or
            displays prejudice based on factors such as race, gender, religion,
            nationality, disability, sexual orientation, age, or any other
            forbidden criteria, or exhibit any other form of objectionable
            material;
          </li>
          <li>
            Engage in stalking, endeavor to take advantage of individuals, or by
            requesting personal information;
          </li>
          <li>
            Provide or contribute false, incorrect, or deceptive information;
          </li>
          <li>
            Assume the identity or make an effort to portray oneself as
            Wrytopia, a member of our staff, another user, or any individual or
            entity, which includes but is not limited to, using email addresses
            or screen names linked to any of the mentioned identities;
          </li>
          <li>
            Transmit or facilitate the transmission of advertisements or
            promotions without obtaining our explicit written permission, engage
            in commercial activities or sales, which encompasses, but is not
            restricted to, activities like "spam," "junk mail," "chain letters,"
            contests, sweepstakes, and other sales-driven promotions, barter
            arrangements, advertising endeavors, or any analogous forms of
            solicitation;
          </li>
          <li>
            Endorse any illegal activities, fraudulent behavior, or advocate,
            support, or aid any actions that are against the law or involve
            deceitful intentions;
          </li>
          <li>
            Convey a false impression that they originate from us or have our
            endorsement, or similarly, from any other individual or entity, when
            such is not the actual scenario.
          </li>
        </ul>
        <br />
        <p>
          Moreover, it is your responsibility to furnish precise instructions
          and information while placing orders, enabling our writers to craft
          content that is fitting and customized. Additionally, we anticipate
          responsible and courteous interaction with our support team and
          writers, which cultivates an environment of cooperation and
          efficiency. Upholding these duties contributes significantly to
          maintaining the integrity and excellence of the services we offer.
        </p>
      </div>
      <div>
        <li class="bold">Proprietary Rights</li>
        <p>
          The ownership and operation of the service belong to Wrytopia. Unless
          expressly stated otherwise by Wrytopia, all elements encompassed
          within or associated with the service, including past, present, and
          forthcoming iterations, domain names, source and object code, textual
          content, website layout, logos, graphics, and icons, as well as all
          registered and unregistered intellectual property rights tied to these
          elements (collectively referred to as "Wrytopia Content"), are under
          the ownership, control, and/or licensing of Wrytopia.
        </p>
        <p>
          Wrytopia Content is safeguarded against unauthorized utilization,
          duplication, and distribution through copyright, trademark, patent,
          and other legal frameworks, regulations, and international agreements.
          Your engagement with the service does not confer upon you any
          ownership or additional entitlements concerning the service or the
          Wrytopia Content. Any application of Wrytopia Content not explicitly
          sanctioned by these Terms is proscribed. Unsanctioned utilization of
          the materials featured on the service may breach copyright, trademark,
          patent, and other pertinent legal guidelines and international
          agreements, potentially leading to legal consequences, both criminal
          and civil.
        </p>
        <p>
          <i>Copyright</i><br />
          All texts, images, diagrams, audio, animations, and video files, along
          with their arrangements, are subject to copyright law and other
          regulations concerning the safeguarding of intellectual property. The
          materials on this platform must not be replicated, modified,
          disseminated, or otherwise shared with third parties, whether for
          commercial purposes or distribution. Similar usage on alternate
          platforms is likewise impermissible.
        </p>
        <p>
          You are not permitted to download the contents of this website
          exclusively for your personal, non-commercial, private use, provided
          you adhere to all copyright, branding, and other protective statutes.
          This entails refraining from erasing or rendering inaccessible any
          pertinent information. Any alterations or utilization of the Website's
          contents for purposes other than those specified constitute a
          violation of our rights and are explicitly prohibited.
        </p>
        <p>
          <i>Trademarks</i><br />
          If not explicitly indicated otherwise, all trademarks mentioned are
          safeguarded by Wrytopia. These are provided protection both on a
          national and international scale, and they must not be duplicated,
          copied, altered, or utilized in any alternative manner without
          obtaining the express prior authorization of Wrytopia.
        </p>
        <p>
          Moreover, other designations, logos, product and service titles,
          designs, images, and catchphrases mentioned or visible belong to the
          trademarks of their respective proprietors. Using any such
          intellectual property, except when specifically authorized, will
          amount to a violation of the rights of the property owner and could
          potentially breach federal or other legislations, leading to possible
          legal action against the offender. These trademarks and associated
          proprietary assets enjoy protection under national and international
          legal frameworks and are not to be reproduced, copied, modified,
          posted, or utilized in any form or manner without securing the express
          prior written consent of Wrytopia.
        </p>
      </div>
      <div>
        <li class="bold">Effectivity</li>
        <p>
          The duration of this agreement will commence upon your initial access
          or utilization of the service, or upon your explicit acceptance of
          this agreement. This arrangement will remain in effect until
          terminated by either party – yourself or us. The termination shall be
          executed through a written notice with a duration of twenty-four (24)
          hours. If the notice is initiated by Wrytopia, it shall be dispatched
          to the last email address you provided to Wrytopia. If the notice is
          initiated by you to Wrytopia, it must be sent exclusively to the
          following email address:
          <a
            href="mailto: team.wrytopia@gmail.com"
            class="link-success"
            target="_blank"
            >team.wrytopia@gmail.com</a
          >, including the following particulars: (i) your username; (ii) the
          email address you provided to us; and (iii) "Termination of Service
          Agreement" in the email's subject line.
        </p>
      </div>
      <div>
        <li class="bold">Services Offered</li>
        <p>
          <span class="bold">Contract C:</span> This service involves a one-time
          fee and is provided by a third-party agent to the writer. It offers
          several benefits, including the creation of your story outline in a
          single session, ensuring a focused and cohesive narrative.
          Additionally, Contract C provides a valuable one-on-one meeting with
          our team of experts to curate the perfect outline tailored to your
          specific needs. What sets this service apart is its budget-friendly
          price range, which is determined based on your story's requirements.
          We are committed to guaranteeing the success of your outline in
          securing contracts.
        </p>
        <p>
          <span class="bold">Contract CE:</span> This exclusive service remains
          in effect until your story is accepted on the online writing platform.
          It offers unlimited revisions for your story outlines, ensuring that
          your narrative is refined to perfection. Just like Contract C, you
          will have the opportunity for a one-on-one meeting with our team,
          ensuring a personalized and top-notch outline. While Contract CE comes
          at a more premium price, it is tailored precisely to your story's
          unique demands. We stand by our commitment to guarantee the success of
          your outline in securing contracts, making Contract CE an ideal choice
          for writers aiming for excellence in the online writing world.
        </p>
      </div>
      <div>
        <li class="bold">Governing Law and Dispute Resolution</li>
        <p>
          These terms are governed by the laws of the country in which you
          reside, and you retain the option to initiate legal actions in the
          courts of your local jurisdiction.
        </p>
      </div>
      <div>
        <li class="bold">Limitation of Liability</li>
        <p>
          Wrytopia's "Limitation of Liability'' clause, delineates the
          boundaries of our responsibility in providing writing services. By
          availing our services, you acknowledge and accept that Wrytopia, its
          affiliates, employees, and associates shall not be held liable for any
          direct, indirect, incidental, consequential, or punitive damages that
          may arise from the use of our content. This limitation extends to any
          disruptions, errors, or inaccuracies in the content, as well as any
          interruptions or delays in service delivery. While we endeavor to
          provide exemplary content, we do not guarantee outcomes or results,
          and our liability is restricted to the extent permitted by law. It is
          your responsibility to assess the content's suitability and accuracy
          for your intended purpose. Through this provision, we aim to be
          transparent about the parameters of our service, while emphasizing the
          importance of informed decision-making on your part.
        </p>
      </div>
      <div>
        <li class="bold">Usage License</li>
        <p>
          By availing of Wrytopi’s services, you are granted a limited,
          non-exclusive, non-transferable license to utilize the content
          provided by our skilled writers for your personal or business use.
          This license extends solely to the specific projects for which our
          services were enlisted and is subject to compliance with our terms.
          You may not reproduce, distribute, modify, or exploit the content
          beyond the original intent without explicit written consent from
          Wrytopia. You hereby agree not to employ automated methods (such as
          scripts, bots, spiders, crawlers, or scrapers) to access, copy, or
          gather content or information from the service, nor are you permitted
          to employ other data mining techniques or processes to frame, conceal,
          extract data, or other materials from the Wrytopia Content unless
          granted explicit authorization by Wrytopia through a distinct written
          agreement. The license granted is contingent upon complete adherence
          to copyright and intellectual property regulations, as well as
          refraining from any unlawful or unethical utilization of the content.
          Wrytopia retains the right to showcase the completed work in our
          portfolio and promotional materials, while ensuring client
          confidentiality regarding sensitive information. This "Usage License"
          underscores our commitment to delivering top-tier written content
          while maintaining the integrity of both our clients and our
          organization.
        </p>
        <p>
          All rights not explicitly bestowed in this context remain reserved.
          Wrytopia retains the right to apply reasonable constraints on your
          access to the service, which may involve limitations on time,
          features, or specific content. You accept and acknowledge that in the
          event of your account's termination or the termination of these Terms,
          the license provided to you concerning the Services will automatically
          expire.
        </p>
      </div>
      <div>
        <li class="bold">Disclaimer</li>
        <p>
          Engaging our writing services implies your acknowledgment that the
          content provided is for general informational purposes only. While we
          endeavor to ensure accuracy, we do not warrant or guarantee the
          completeness, reliability, or timeliness of the content. Wrytopia will
          not be held liable for any direct or indirect damages, losses, or
          inconveniences arising from the use or reliance upon the provided
          information. It is incumbent upon you, the client, to independently
          verify and assess the suitability of the content for your specific
          needs. Our writing services are a resource designed to assist, but
          ultimate decisions and actions based on the content are your sole
          responsibility. This section underscores our commitment to
          transparency and empowers you with the knowledge to make informed
          choices when utilizing our services.
        </p>
      </div>
      <div>
        <li class="bold">Termination</li>
        <p>
          We reserve the right to terminate our services if you breach any
          stipulated terms or engage in actions contrary to ethical standards or
          applicable laws. Furthermore, either party retains the right to
          terminate a project or engagement for any reason, with written notice
          provided to the other party. Upon termination, you agree to cease all
          use of the provided content and any associated materials. Wrytopia is
          not liable for any damages, losses, or inconveniences arising from
          such termination. It is essential to note that any outstanding
          payments for completed work shall remain due even after termination.
          This portion of our Terms and Conditions reinforces our commitment to
          maintaining a professional and ethical relationship, while clarifying
          the mechanisms for concluding our engagement, if necessary.
        </p>
      </div>
      <div>
        <li class="bold">Indemnity</li>
        <p>
          You hereby agree to indemnify and hold harmless Wrytopia, its
          affiliates, employees, and associates from any claims, damages,
          losses, or liabilities that may arise from the use of our provided
          content. This indemnity extends to actions taken based on the content,
          any breach of terms, or violation of intellectual property rights. It
          is imperative that you exercise due diligence in assessing the
          content's suitability for your intended purpose, as we bear no
          responsibility for consequences resulting from its application. You
          will promptly reimburse us, upon request, for any payments we make
          concerning any damages, liabilities, costs, losses, or expenses
          covered by the aforementioned indemnity. During the assessment period
          of any claim, request, or action, we may, at our discretion, withhold
          disbursement of any funds otherwise due to you as per this agreement,
          provided the amount withheld does not exceed your potential
          accountability to us as stipulated in this paragraph. Wrytopia strives
          to deliver excellence in every facet of our services, but it is
          incumbent upon you to utilize the content responsibly and within legal
          bounds. Through this clause, we affirm our commitment to transparency,
          while delineating the shared responsibilities in our client-provider
          relationship.
        </p>
      </div>
      <div>
        <li class="bold">Modification of Terms</li>
        <p>
          We retain the prerogative to amend, update, or revise these terms at
          any time to reflect changes in our services, industry regulations, or
          the evolving needs of our clients. Any modifications will be
          communicated through conspicuous means, such as email notifications or
          announcements on our platform. Your continued engagement with our
          services following the implementation of such changes constitutes your
          acceptance of the revised terms. We recommend regular review of these
          terms to remain informed about any modifications that might affect
          your engagement with Wrytopia. Through this provision, we demonstrate
          our commitment to transparency and the seamless evolution of our
          client-provider relationship.
        </p>
      </div>
      <div>
        <li class="bold">Severability</li>
        <p>
          Except where explicitly indicated within the Terms, in the event that
          any provision of the Terms is deemed invalid, unlawful, or
          unenforceable, all other provisions will remain fully valid and
          effective. This "Severability" clause remains applicable even after
          the services have expired or been terminated.
        </p>
      </div>
      <div>
        <li class="bold">Entire Agreement</li>
        <p>
          The Terms, along with any referenced or incorporated terms or
          documents, constitute the complete agreement between you and Wrytopia
          concerning the Services. These terms cannot be altered without the
          approval of a duly authorized representative of Wrytopia and will take
          precedence over any terms or conditions you may include in documents
          or communications with us, irrespective of whether Wrytopia signs them
          or refrains from objecting. This "Entire Agreement" provision remains
          applicable even after the Services have expired or been terminated.
        </p>
      </div>
      <div>
        <li class="bold">Waiver</li>
        <p>
          Even in cases where there's a delay in enforcing these Terms, we still
          retain the right to enforce them subsequently. Our decision not to
          promptly demand your compliance with any obligations stipulated in
          these Terms, or any delay in initiating actions against you for
          violating these Terms, shall not imply your exemption from fulfilling
          those obligations. Nor will it hinder us from pursuing actions against
          you at a later time.
        </p>
      </div>
      <div>
        <li class="bold">Third Party Content</li>
        <p>
          These terms form a contractual agreement between you and us, and no
          other individual shall possess the entitlement to enforce any of these
          Terms.
        </p>
        <p>
          For your convenience, our writers may offer links or references to
          external websites. We make no assertions regarding the content of any
          other websites or applications that might be accessible. If you opt to
          access such websites or applications, you undertake the associated
          risk. We lack control over the content of these third-party sites or
          applications and disclaim any liability for any losses or damages
          resulting from your utilization of them. Your interaction with such
          third-party sites is governed by their respective Terms and
          Conditions.
        </p>
        <p>
          These links to third-party sites might encompass connections to
          certain social media functions that allow you to share or transmit
          specific limited content from this website or application via certain
          third-party platforms. You may solely utilize these functions when
          they are supplied by us and solely in relation to the specified
          content. The availability of these functions and links to third-party
          sites is contingent on any additional Terms provided by us concerning
          these functions.
        </p>
      </div>
      <div>
        <li class="bold">Additional Assistance</li>
        <p>
          If you have inquiries regarding the Services or these Terms and
          Conditions, kindly reach out to us through email:
          <a
            href="mailto: team.wrytopia@gmail.com"
            class="link-success"
            target="_blank"
            >team.wrytopia@gmail.com</a
          >
        </p>
      </div>
    </ol>
    <h5 class="text-center mt-5">
      Thank you for reading our Terms and Conditions. Best regards!
    </h5>
  </div>
</template>

<script>
export default{
  name: 'TermsAndConditionsPage'
}
</script>

<style scoped>
.container {
  min-height: 100vh;
  margin-top: 150px;
  text-align: justify;
}

* {
  color: black !important;
  font-family: "Poppins" !important;
  font-weight: 400 !important;
  line-height: 200% !important;
}

strong {
  font-weight: 800 !important;
}

a {
  color: #00b18f !important;
}

.bold {
  font-weight: bold !important;
}
</style>
